import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Filter, Search, X, ChevronDown, Calendar, MapPin, Layers, Loader2, Shapes } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Slider } from '../../components/ui/slider';
import { fetchArtworks, fetchArtCategories, type ArtWork, type ArtCategory } from '../../services/portfolioService';
import { useQuery } from '@tanstack/react-query';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { useTenantContext } from '../../context/TenantContext';
import { useDomainQuery, getDomainFilterParams } from '../../hooks/useDomainQuery';

// Animations (conservées, elles sont excellentes)
const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.07 } } };
const cardVariants = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4 } } };
const imageHoverVariants = { rest: { scale: 1 }, hover: { scale: 1.08 } };
const overlayHoverVariants = { rest: { opacity: 0.8 }, hover: { opacity: 1 } };
const titleHoverVariants = { rest: { y: 0 }, hover: { y: -5 } };

export const PortfolioHome = () => {
  const { tenant } = useTenantContext();
  
  // Utiliser la logique de domaine
  const { getFilterParams } = useDomainQuery();
  const { domain } = getFilterParams();
  
  const [searchInput, setSearchInput] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(window.innerWidth >= 768); // true par défaut sur desktop (md: 768px et plus)
  const [displayCount, setDisplayCount] = useState(9);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [advancedFilters, setAdvancedFilters] = useState({ years: [], materials: [], locations: [] });
  const [availableYears, setAvailableYears] = useState<string[]>([]);
  const [availableMaterials, setAvailableMaterials] = useState<string[]>([]);
  const [availableLocations, setAvailableLocations] = useState<string[]>([]);

  // Récupérer les catégories avec logique de domaine
  const { data: categories, isLoading: categoriesLoading } = useQuery({ 
    queryKey: ['artCategories', domain], 
    queryFn: () => fetchArtCategories() 
  });
  
  // Récupérer les œuvres avec logique de domaine
  const { data: artworks, isLoading: artworksLoading } = useQuery({ 
    queryKey: ['artworks', domain], 
    queryFn: () => fetchArtworks() 
  });

  useEffect(() => {
    if (artworks) {
      setAvailableYears([...new Set(artworks.map(art => art.year).filter(Boolean) as string[])].sort().reverse());
      setAvailableMaterials([...new Set(artworks.flatMap(art => art.materials || []))].filter(Boolean));
      setAvailableLocations([...new Set(artworks.map(art => art.location).filter(Boolean) as string[])]);
    }
  }, [artworks]);

  const filteredArtworks = React.useMemo(() => {
    return artworks?.filter(artwork => {
      const searchMatch = !searchInput || artwork.title.toLowerCase().includes(searchInput.toLowerCase()) || artwork.description?.toLowerCase().includes(searchInput.toLowerCase());
      const categoryMatch = selectedCategories.length === 0 || artwork.categories?.some(cat => selectedCategories.includes(cat.id));
      const yearMatch = advancedFilters.years.length === 0 || (artwork.year && advancedFilters.years.includes(artwork.year));
      const materialMatch = advancedFilters.materials.length === 0 || artwork.materials?.some(mat => advancedFilters.materials.includes(mat));
      const locationMatch = advancedFilters.locations.length === 0 || (artwork.location && advancedFilters.locations.includes(artwork.location));
      return searchMatch && categoryMatch && yearMatch && materialMatch && locationMatch;
    }) || [];
  }, [artworks, searchInput, selectedCategories, advancedFilters]);

  const handleFilterToggle = (filterType: 'categories' | 'years' | 'materials' | 'locations', value: string) => {
    if (filterType === 'categories') setSelectedCategories(prev => prev.includes(value) ? prev.filter(id => id !== value) : [...prev, value]);
    else setAdvancedFilters(prev => ({ ...prev, [filterType]: prev[filterType].includes(value) ? prev[filterType].filter(item => item !== value) : [...prev[filterType], value] }));
  };

  const resetFilters = () => {
    setSearchInput('');
    setSelectedCategories([]);
    setAdvancedFilters({ years: [], materials: [], locations: [] });
    setDisplayCount(9);
  };

  const handleLoadMore = () => {
    setLoadingMore(true);
    setTimeout(() => {
      setDisplayCount(prev => prev + 9);
      setLoadingMore(false);
    }, 500);
  };

  const hasActiveFilters = searchInput || selectedCategories.length > 0 || advancedFilters.years.length > 0 || advancedFilters.materials.length > 0 || advancedFilters.locations.length > 0;

  // Configuration SEO personnalisée par tenant
  const seoConfig = React.useMemo(() => {
    const seo = tenant?.config?.seo || {};
    const portfolioHeader = tenant?.config?.pages?.portfolio?.header || {};
    const currentDomain = window.location.hostname;
    
    return {
      title: `${portfolioHeader.title || 'Portfolio'} | ${tenant?.name || 'Portfolio'}`,
      description: seo.description || portfolioHeader.line1 || `Explorez le portfolio complet de ${tenant?.name || 'l\'artiste'}.`,
      image: seo.ogImage || tenant?.logo || tenant?.icon,
      url: `https://${currentDomain}/portfolio`
    };
  }, [tenant]);

  return (
    <PublicLayout>
      <SEOMetaTags
        title={seoConfig.title}
        description={seoConfig.description}
        image={seoConfig.image}
        url={seoConfig.url}
      />
      <div className="bg-black text-white relative overflow-hidden pt-20">
        <div className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover" style={{
          backgroundImage: tenant ? `url(${tenant.config.bg})` : 'url(/default-bg.jpg)',
          backgroundAttachment: 'fixed'
        }}>
          <div className="absolute inset-0 bg-gradient-to-b from-black/95 via-black/80 to-black/95"></div>
        </div>

        <div className="relative isolate overflow-hidden pt-8 sm:pt-12 pb-16">
          <div className="mx-auto max-w-4xl px-6 lg:px-8 text-center">
            <motion.div 
              className="relative"
              initial={{ opacity: 0, y: 30 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-5xl md:text-6xl font-bold tracking-tight text-white sm:text-7xl mb-8">
                <span className="relative inline-block">
                  <span className="absolute -inset-x-6 -inset-y-2 bg-gradient-to-r from-red-500/20 via-red-700/20 to-transparent -z-10 rounded-full blur-lg"></span>
                  <span className="bg-clip-text text-transparent bg-[linear-gradient(90deg,#fecaca_0%,#f87171_50%,#dc2626_100%)] bg-[length:200%_auto] animate-gradient">
                    ŒUVRES
                  </span>
                </span>
              </h1>
              <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-48 h-1 bg-gradient-to-r from-transparent via-red-500/50 to-transparent"></div>
              
              <div className="relative mt-8">
                <p className="text-xl md:text-2xl font-medium text-gray-300 max-w-3xl mx-auto leading-relaxed">
                  <span className="relative">
                    <span className="absolute -inset-1 bg-gradient-to-r from-red-500/10 to-transparent rounded-lg blur-sm"></span>
                    <span className="relative">Là où la matière prend forme et raconte une histoire</span>
                  </span>
                  <span className="block mt-2 text-red-300/80 font-light">Explorez une collection d'œuvres qui défient le temps</span>
                </p>
              </div>
              
              <div className="absolute inset-x-0 -top-16 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-24" aria-hidden="true">
                <div className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-red-900 to-red-700 opacity-20 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]" style={{
                  clipPath: 'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
                }}></div>
              </div>
            </motion.div>
          </div>
        </div>

        <div className="relative z-10 container mx-auto px-4 pt-2 md:pt-4 pb-4">
          <div className="mb-12 bg-black/30 backdrop-blur-sm rounded-lg border border-white/10 p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Input 
                  type="text" 
                  placeholder="Rechercher une œuvre, un lieu, un matériau..." 
                  className="pr-10 border-gray-600 focus:border-gray-600 bg-black" 
                  value={searchInput} 
                  onChange={(e) => setSearchInput(e.target.value)} 
                />
                <Search size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50" />
              </div>
              <Button variant="outline" onClick={() => setShowAdvancedFilters(!showAdvancedFilters)} className="flex items-center gap-2">
                <Filter size={16} /> Filtres avancés <ChevronDown size={16} className={`transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`} />
              </Button>
            </div>
            {showAdvancedFilters && (
              <div className="mt-6 border-t border-white/10 pt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <FilterGroup title="Catégories" icon={Layers} items={categories || []} selected={selectedCategories} onToggle={(id) => handleFilterToggle('categories', id)} />
                <FilterGroup title="Années" icon={Calendar} items={availableYears} selected={advancedFilters.years} onToggle={(year) => handleFilterToggle('years', year)} />
                <FilterGroup title="Matériaux" icon={Shapes} items={availableMaterials} selected={advancedFilters.materials} onToggle={(mat) => handleFilterToggle('materials', mat)} />
                <FilterGroup title="Lieux" icon={MapPin} items={availableLocations} selected={advancedFilters.locations} onToggle={(loc) => handleFilterToggle('locations', loc)} />
              </div>
            )}
            {hasActiveFilters && (
              <div className="mt-6 border-t border-white/10 pt-4 flex items-center justify-between">
                <span className="text-white/80 text-sm">{filteredArtworks.length} œuvre(s) trouvée(s)</span>
                <Button onClick={resetFilters} variant="link" className="text-red-400 p-0 h-auto">Réinitialiser</Button>
              </div>
            )}
          </div>

          {artworksLoading ? <LoadingGrid /> :
            filteredArtworks.length > 0 ? (
              <>
                <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" variants={containerVariants} initial="hidden" animate="visible">
                  {filteredArtworks.slice(0, displayCount).map(artwork => <ArtworkCard key={artwork.id} artwork={artwork} />)}
                </motion.div>
                {displayCount < filteredArtworks.length && (
                  <div className="mt-12 text-center">
                    <Button onClick={handleLoadMore} variant="destructive" size="lg" disabled={loadingMore}>
                      {loadingMore ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Chargement...</> : `Afficher plus (${filteredArtworks.length - displayCount})`}
                    </Button>
                  </div>
                )}
              </>
            ) : <p className="text-center py-20 text-white/70 text-xl">Aucune œuvre ne correspond à vos critères.</p>
          }
        </div>
      </div>
    </PublicLayout>
  );
};

const FilterGroup = ({ title, icon: Icon, items, selected, onToggle }: any) => (
  <div className="space-y-3">
    <h3 className="font-medium text-white flex items-center gap-2"><Icon size={16} className="text-red-500" />{title}</h3>
    <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
      {items.map((item: any) => {
        const id = typeof item === 'object' ? item.id : item;
        const name = typeof item === 'object' ? item.name : item;
        return (
          <div key={id} className="flex items-center space-x-2">
            <Checkbox id={id} checked={selected.includes(id)} onCheckedChange={() => onToggle(id)} />
            <label htmlFor={id} className="text-sm text-white/90 cursor-pointer select-none">{name}</label>
          </div>
        );
      })}
    </div>
  </div>
);

const ArtworkCard = ({ artwork }: { artwork: ArtWork }) => {
  const { tenant } = useTenantContext();
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.div 
      className="group relative artwork-card"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.6 }}
    >
      <Link to={`/portfolio/${artwork.slug}`} className="block h-full">
        <div 
          className="relative overflow-hidden rounded-lg aspect-square shadow-2xl transition-all duration-700 group-hover:shadow-[0_20px_50px_rgba(0,0,0,0.4)] h-full"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{
            transformStyle: 'preserve-3d',
            backfaceVisibility: 'hidden',
          }}
        >
          <motion.div
            className="w-full h-full relative"
            initial={false}
            animate={{
              scale: isHovered ? 1.05 : 1,
              transition: { duration: 0.5, ease: [0.4, 0, 0.2, 1] }
            }}
          >
            <motion.img 
              src={artwork.featured_image_url} 
              alt={artwork.title} 
              className="w-full h-full object-cover"
              loading="lazy"
              style={{
                transform: isHovered ? 'scale(1.1)' : 'scale(1)',
                transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            />
            
            <motion.div 
              className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent"
              initial={{ opacity: 0.6 }}
              animate={{ opacity: isHovered ? 0.8 : 0.6 }}
              transition={{ duration: 0.3 }}
            />
            
            <div className="absolute inset-0 p-6 flex flex-col justify-end">
              <motion.h3 
                className="text-2xl font-bold text-white mb-2 font-heading"
                initial={{ y: 20, opacity: 0 }}
                animate={{ 
                  y: isHovered ? 0 : 20,
                  opacity: isHovered ? 1 : 0 
                }}
                transition={{ delay: 0.1, duration: 0.3 }}
              >
                {artwork.title}
              </motion.h3>
              
              <motion.div 
                className="flex flex-wrap gap-2"
                initial={{ y: 20, opacity: 0 }}
                animate={{ 
                  y: isHovered ? 0 : 20,
                  opacity: isHovered ? 1 : 0 
                }}
                transition={{ delay: 0.2, duration: 0.3 }}
              >
                {artwork.categories?.map(cat => (
                  <Badge key={cat.id} className="bg-red-600 hover:bg-red-700 text-white border-0">
                    {cat.name}
                  </Badge>
                ))}
                {artwork.year && (
                  <Badge variant="outline" className="text-white border-white/30 bg-black/30">
                    {artwork.year}
                  </Badge>
                )}
              </motion.div>
              
              {artwork.materials && (
                <motion.div 
                  className="mt-3 text-sm text-white/80 flex items-center gap-1"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ 
                    y: isHovered ? 0 : 20,
                    opacity: isHovered ? 1 : 0 
                  }}
                  transition={{ delay: 0.3, duration: 0.3 }}
                >
                  <Shapes size={14} className="text-red-400" />
                  <span>{artwork.materials.join(', ')}</span>
                </motion.div>
              )}
              
              {/* Logo en bas à droite */}
              <motion.div
                className="absolute bottom-4 right-4 w-16 h-16 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{
                  opacity: isHovered ? 1 : 0.7,
                  scale: isHovered ? 1 : 0.9
                }}
                transition={{ duration: 0.3 }}
              >
                <img 
                  src={tenant?.config?.icon ? tenant.config.icon : ''} 
                  alt="Logo" 
                  className="w-10 h-10 object-contain"
                  loading="lazy"
                />
              </motion.div>
            </div>
          </motion.div>
          
          {/* Effet de brillance au survol */}
          <motion.div 
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100"
            style={{
              transform: 'translateX(-100%)',
              transition: 'transform 1s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
            animate={{
              transform: isHovered ? 'translateX(100%)' : 'translateX(-100%)',
            }}
          />
        </div>
      </Link>
    </motion.div>
  );
};

const LoadingGrid = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
    {Array.from({ length: 6 }).map((_, i) => <div key={i} className="bg-black/30 rounded-lg aspect-square animate-pulse" />)}
  </div>
);

export default PortfolioHome;